import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CreditCard, RotateCcw, KeyRound, Activity, Download } from "lucide-react";

const MONEY_LABELS = {
  cancellation_fee: "Cancellation fee applied",
  extension_payment: "Extension payment recorded",
  charge_capture: "Card on file charged",
  refund: "Refund issued",
  deposit_hold: "Deposit hold placed",
  deposit_release: "Deposit hold released",
  deposit_charge: "Deposit captured",
  deposit_deduct: "Deposit deducted",
  charges_added: "Post-rental charges added",
  refund_reversal: "Refund mark removed",
};

const MONEY_OUT = (a) => a === "refund" || a === "deposit_release";

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

const METHOD_LABEL = { paypal: "PayPal", card: "Card (Stripe)", zelle: "Zelle", manual_card: "Card (manual)", wire: "Bank wire" };
const KIND_LABEL = { booking: "Booking", extension: "Extension", upgrade: "Upgrade" };

export default function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  const [extStats, setExtStats] = useState(null);
  const [split, setSplit] = useState(null);
  const [stripeCfg, setStripeCfg] = useState(null);
  const [stripeKey, setStripeKey] = useState("");
  const [stripeSaving, setStripeSaving] = useState(false);
  const [moneyFeed, setMoneyFeed] = useState([]);
  const [wireOn, setWireOn] = useState(true);
  const [wireSaving, setWireSaving] = useState(false);

  const load = () => {
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/admin/financial-audit").then((r) => setMoneyFeed(r.data)).catch(() => {});
    api.get("/admin/extension-stats").then((r) => setExtStats(r.data)).catch(() => {});
    api.get("/admin/payments/split").then((r) => setSplit(r.data)).catch(() => {});
    api.get("/admin/settings/stripe").then((r) => setStripeCfg(r.data)).catch(() => {});
    api.get("/admin/settings/payment-options").then((r) => setWireOn(r.data.wire_enabled !== false)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveStripeKey = async () => {
    if (!stripeKey.trim()) return toast.error("Paste your Stripe secret key first");
    setStripeSaving(true);
    try {
      const { data } = await api.put("/admin/settings/stripe", { secret_key: stripeKey.trim() });
      toast.success(`Stripe ${data.mode} key saved (${data.masked}) — live instantly, no restart needed`);
      setStripeKey("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setStripeSaving(false);
    }
  };

  const removeStripeKey = async () => {
    if (!window.confirm("Remove the panel key? The server environment key takes over again.")) return;
    try {
      await api.delete("/admin/settings/stripe");
      toast.success("Panel key removed — server environment key active");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleRefund = async (p) => {
    const marking = !p.refunded;
    const note = marking ? window.prompt("Refund note (optional):", "") : null;
    if (marking && note === null) return;
    try {
      await api.patch(`/admin/payments/${p.id}`, { refunded: marking, refund_note: note || "" });
      toast.success(marking ? "Marked as refunded" : "Refund flag removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const todayStr = new Date().toDateString();
  const todayISO = new Date().toLocaleDateString("en-CA");
  const todayEntries = moneyFeed.filter((m) => new Date(m.created_at).toDateString() === todayStr);
  const todayNet = todayEntries
    .filter((m) => !["deposit_hold", "charges_added"].includes(m.action))
    .reduce((s, m) => s + (MONEY_OUT(m.action) ? -(Number(m.amount) || 0) : (Number(m.amount) || 0)), 0);
  const [dayOpen, setDayOpen] = useState(false);
  const [pickDate, setPickDate] = useState("");
  const [pickEntries, setPickEntries] = useState(null);
  const loadDay = async (d) => {
    setPickDate(d);
    if (!d || d === todayISO) { setPickEntries(null); return; }
    try {
      const { data } = await api.get(`/admin/financial-audit?date=${d}&tz_offset=${new Date().getTimezoneOffset()}`);
      setPickEntries(data);
    } catch { setPickEntries([]); }
  };

  const exportDayCsv = () => {
    const day = pickDate || todayISO;
    const header = ["date", "booking_ref", "customer_email", "action", "description", "amount_usd", "signed_usd", "counted_in_net", "actor", "detail"];
    const rows = dayEntries.map((m) => {
      const counted = !["deposit_hold", "charges_added"].includes(m.action);
      const amt = m.amount != null ? Number(m.amount).toFixed(2) : "";
      const signed = !counted || m.amount == null ? "" : (MONEY_OUT(m.action) ? -Number(m.amount) : Number(m.amount)).toFixed(2);
      return [day, m.booking_ref, m.customer_email || "", m.action, MONEY_LABELS[m.action] || m.action, amt, signed, counted ? "yes" : "no", m.actor || "", m.detail || ""]
        .map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",");
    });
    const blob = new Blob([[header.map((h) => `"${h}"`).join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `easydrive-money-${day}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} movement${rows.length === 1 ? "" : "s"} for ${day}`);
  };
  const dayEntries = pickDate && pickDate !== todayISO ? (pickEntries || []) : todayEntries;
  const dayNet = dayEntries
    .filter((m) => !["deposit_hold", "charges_added"].includes(m.action))
    .reduce((s, m) => s + (MONEY_OUT(m.action) ? -(Number(m.amount) || 0) : (Number(m.amount) || 0)), 0);
  const dayGroups = Object.values(
    dayEntries.reduce((acc, m) => {
      const g = acc[m.booking_ref] || (acc[m.booking_ref] = { ref: m.booking_ref, id: m.booking_id, email: m.customer_email, entries: [], net: 0 });
      g.entries.push(m);
      if (!["deposit_hold", "charges_added"].includes(m.action)) g.net += MONEY_OUT(m.action) ? -(Number(m.amount) || 0) : (Number(m.amount) || 0);
      return acc;
    }, {})
  );

  const paid = payments.filter((p) => p.payment_status === "paid");
  const collected = paid.filter((p) => !p.refunded).reduce((s, p) => s + (p.amount || 0), 0);
  const refundedTotal = paid.filter((p) => p.refunded).reduce((s, p) => s + (p.amount || 0), 0) + (split?.marked_refunded || 0);
  const toggleOfflineRefund = async (p) => {
    const marking = !p.refunded;
    if (marking && !window.confirm(`Mark ${p.booking_ref} as refunded? The rental total (${fmtMoney(p.amount)}) is logged as refunded in Money activity — issue the actual refund through your bank/provider.`)) return;
    try {
      await api.post(`/admin/bookings/${p.booking_id}/payment-status`, { status: marking ? "refunded" : "paid" });
      toast.success(marking ? "Marked as refunded" : "Refund flag removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const splitTotal = split ? split.card + split.paypal + (split.offline || 0) : 0;

  return (
    <div data-testid="admin-payments-tab">
      <AdminHeader testid="admin-header-payments" eyebrow="Finance" title="Payments" description="Every online transaction — PayPal and card — with booking refs and refund tracking." />

      <div data-testid="payment-options-card" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Checkout payment options</p>
          <p className="text-xs text-muted-foreground font-ui mt-0.5">Show or hide Bank Wire Transfer at guest checkout. Existing wire bookings are unaffected.</p>
        </div>
        <button
          data-testid="wire-toggle-button"
          onClick={async () => {
            setWireSaving(true);
            try {
              const { data } = await api.put("/admin/settings/payment-options", { wire_enabled: !wireOn });
              setWireOn(data.wire_enabled);
              toast.success(data.wire_enabled ? "Bank wire enabled at checkout" : "Bank wire hidden from checkout");
            } catch (e) { toast.error(errMsg(e)); } finally { setWireSaving(false); }
          }}
          disabled={wireSaving}
          className={`rounded-full px-5 h-9 text-sm font-ui transition-colors duration-200 disabled:opacity-50 ${wireOn ? "bg-[#C71E2E] text-white" : "border border-[#E5DED5] text-foreground/70 hover:border-[#C71E2E]/50"}`}
        >
          {wireSaving ? "Saving…" : wireOn ? "Bank wire: ON" : "Bank wire: OFF"}
        </button>
      </div>

      {moneyFeed.length > 0 && (
        <div data-testid="money-activity-strip" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5 mb-6">
          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <Activity size={13} className="text-[#C71E2E]" /> Money activity — every dollar that moved, across all bookings
            <button type="button" data-testid="money-today-total" onClick={() => { setDayOpen(true); loadDay(todayISO); }} title="Open today's per-booking breakdown" className={`ml-auto font-display text-xl tracking-tight normal-case cursor-pointer hover:opacity-75 transition-opacity underline decoration-dotted underline-offset-4 decoration-[#C71E2E]/40 ${todayNet >= 0 ? "text-emerald-700" : "text-[#C71E2E]"}`}>
              {todayNet < 0 ? "−" : ""}{fmtMoney(Math.abs(todayNet))} <span className="text-[10px] font-ui tracking-[0.15em] uppercase text-muted-foreground">net today</span>
            </button>
          </p>
          <ul className="mt-3 divide-y divide-[#F5EFE6] max-h-64 overflow-y-auto pr-1">
            {moneyFeed.map((m) => (
              <li key={m.id} data-testid={`money-activity-${m.id}`} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-ui">
                <span className="text-muted-foreground shrink-0">{new Date(m.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                <Link to={`/admin?tab=bookings&booking=${m.booking_id}`} data-testid={`money-activity-ref-${m.id}`} className="font-semibold text-[#C71E2E] hover:underline">{m.booking_ref}</Link>
                <span className="text-[#1A1A1A]">{MONEY_LABELS[m.action] || m.action}</span>
                {m.amount != null && (
                  <span className={`font-semibold ${MONEY_OUT(m.action) ? "text-emerald-700" : "text-[#8f1f2b]"}`}>
                    {MONEY_OUT(m.action) ? "−" : ""}${Number(m.amount).toFixed(2)}
                  </span>
                )}
                {m.detail && <span className="text-muted-foreground truncate max-w-[280px]">{m.detail}</span>}
                <span className="text-muted-foreground ml-auto shrink-0">by {m.actor}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={dayOpen} onOpenChange={setDayOpen}>
        <DialogContent data-testid="money-day-modal" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Money moved{pickDate && pickDate !== todayISO ? "" : " today"} — {new Date((pickDate || todayISO) + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</DialogTitle>
            <div className="mt-2 flex items-center gap-2.5">
              <label className="text-[10px] font-ui tracking-[0.15em] uppercase text-muted-foreground" htmlFor="money-day-picker">Reconcile day</label>
              <input id="money-day-picker" data-testid="money-day-picker" type="date" value={pickDate || todayISO} max={todayISO} onChange={(e) => loadDay(e.target.value)} className="px-3 py-1.5 rounded-full border border-[#E5DED5] bg-white text-xs font-ui focus:outline-none focus:ring-2 focus:ring-[#C71E2E]/30" />
              {pickDate && pickDate !== todayISO && (
                <button data-testid="money-day-back-today" onClick={() => loadDay(todayISO)} className="text-[11px] font-ui text-[#C71E2E] hover:underline">Back to today</button>
              )}
              <button data-testid="money-day-export" onClick={exportDayCsv} disabled={!dayEntries.length} title="Download this day as CSV for QuickBooks or your accountant" className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-ui text-[#C71E2E] hover:underline disabled:opacity-40 disabled:no-underline">
                <Download size={12} /> Export CSV
              </button>
            </div>
          </DialogHeader>
          {dayGroups.length === 0 ? (
            <p data-testid="money-day-empty" className="text-sm font-ui text-muted-foreground">No cash movement recorded{pickDate && pickDate !== todayISO ? " on this day" : " today"}.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {dayGroups.map((g) => (
                <div key={g.ref} data-testid={`money-day-booking-${g.ref}`} className="rounded-xl border border-[#E5DED5] p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/admin?tab=bookings&booking=${g.id}`} data-testid={`money-day-link-${g.ref}`} className="font-ui text-sm font-semibold text-[#C71E2E] hover:underline">{g.ref}</Link>
                    {g.email && <span className="text-xs font-ui text-muted-foreground">{g.email}</span>}
                    <span className={`ml-auto font-display text-lg ${g.net >= 0 ? "text-emerald-700" : "text-[#C71E2E]"}`}>{g.net < 0 ? "−" : ""}{fmtMoney(Math.abs(g.net))}</span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {g.entries.map((m) => {
                      const counted = !["deposit_hold", "charges_added"].includes(m.action);
                      return (
                        <li key={m.id} className="text-[11px] font-ui text-[#4A4A4A] flex flex-wrap gap-x-2">
                          <span className="text-muted-foreground">{new Date(m.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                          <span>{MONEY_LABELS[m.action] || m.action}{!counted ? " (not collected)" : ""}</span>
                          {m.amount != null && <span className={counted ? (MONEY_OUT(m.action) ? "text-emerald-700" : "text-[#8f1f2b]") : "text-muted-foreground"}>{counted && MONEY_OUT(m.action) ? "−" : ""}${Number(m.amount).toFixed(2)}</span>}
                          <span className="text-muted-foreground ml-auto">by {m.actor}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-center justify-between border-t border-[#E5DED5] pt-3">
            <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">Net today (charges − refunds)</p>
            <p data-testid="money-day-total" className={`font-display text-2xl tracking-tight ${dayNet >= 0 ? "text-emerald-700" : "text-[#C71E2E]"}`}>{dayNet < 0 ? "−" : ""}{fmtMoney(Math.abs(dayNet))}</p>
          </div>
        </DialogContent>
      </Dialog>

      <div data-testid="stripe-key-card" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5 mb-6">
        <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
          <KeyRound size={13} className="text-[#C71E2E]" /> Stripe API Key
        </p>
        <p data-testid="stripe-key-current" className="mt-2 font-ui text-sm text-[#1A1A1A]">
          {stripeCfg
            ? stripeCfg.configured
              ? <>
                  <span className="font-mono">{stripeCfg.masked}</span>
                  <span className={`ml-2 text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-2.5 py-0.5 ${stripeCfg.mode === "live" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-amber-700 border-amber-200 bg-amber-50"}`} data-testid="stripe-key-mode">{stripeCfg.mode}</span>
                  <span className="ml-2 text-xs text-muted-foreground" data-testid="stripe-key-source">via {stripeCfg.source}{stripeCfg.updated_by ? ` · set by ${stripeCfg.updated_by}` : ""}</span>
                </>
              : "Not configured"
            : "Loading…"}
        </p>
        <p className="mt-1 text-xs text-muted-foreground font-ui leading-relaxed">
          Paste your live secret key (sk_live_…) once your US entity is set up — it is verified with Stripe, stored in your database (never shown in full), and takes effect instantly with no server restart.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Input
            data-testid="stripe-key-input"
            value={stripeKey}
            onChange={(e) => setStripeKey(e.target.value)}
            placeholder="sk_live_…"
            autoComplete="off"
            className="flex-1 min-w-[240px] font-mono text-sm"
          />
          <Button data-testid="stripe-key-save-button" onClick={saveStripeKey} disabled={stripeSaving || !stripeKey.trim()} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui disabled:opacity-40">
            {stripeSaving ? "Verifying…" : "Save Key"}
          </Button>
          {stripeCfg?.source === "admin panel" && (
            <Button data-testid="stripe-key-remove-button" variant="outline" onClick={removeStripeKey} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui">
              Remove Override
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5">
          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">Collected (net)</p>
          <p data-testid="payments-total-collected" className="mt-2 font-display text-3xl text-[#1A1A1A]">{fmtMoney(collected)}</p>
        </div>
        <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5">
          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">Transactions</p>
          <p data-testid="payments-total-count" className="mt-2 font-display text-3xl text-[#1A1A1A]">{payments.length}</p>
        </div>
        <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5">
          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">Refunded</p>
          <p data-testid="payments-total-refunded" className="mt-2 font-display text-3xl text-[#C71E2E]">{fmtMoney(refundedTotal)}</p>
        </div>
        <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5">
          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">Extension Upsells — This Month</p>
          <p data-testid="payments-extension-count" className="mt-2 font-display text-3xl text-[#1A1A1A]">{extStats ? extStats.extensions : "—"}</p>
          <p data-testid="payments-extension-detail" className="mt-1 text-xs text-muted-foreground font-ui">
            {extStats
              ? `${fmtMoney(extStats.revenue)} earned${extStats.conversion !== null && extStats.conversion !== undefined ? ` · ${extStats.conversion}% of ${extStats.reminders_sent} return texts` : extStats.reminders_sent === 0 ? " · no return texts yet" : ""}`
              : ""}
          </p>
        </div>
      </div>

      {split && (
        <div data-testid="payments-split-card" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5 mb-6">
          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">This month — {split.month}</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="flex items-baseline justify-between">
                <span className="font-ui text-sm text-[#1A1A1A]">Card (Stripe)</span>
                <span data-testid="payments-split-card-amount" className="font-display text-xl text-[#1A1A1A]">{fmtMoney(split.card)}</span>
              </div>
              <p className="text-xs text-muted-foreground font-ui">{split.card_count} payment{split.card_count === 1 ? "" : "s"}</p>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <span className="font-ui text-sm text-[#1A1A1A]">PayPal</span>
                <span data-testid="payments-split-paypal-amount" className="font-display text-xl text-[#1A1A1A]">{fmtMoney(split.paypal)}</span>
              </div>
              <p className="text-xs text-muted-foreground font-ui">{split.paypal_count} payment{split.paypal_count === 1 ? "" : "s"}</p>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <span className="font-ui text-sm text-[#1A1A1A]">Wire / Manual</span>
                <span data-testid="payments-split-offline-amount" className="font-display text-xl text-[#1A1A1A]">{fmtMoney(split.offline || 0)}</span>
              </div>
              <p className="text-xs text-muted-foreground font-ui">{split.offline_count || 0} payment{(split.offline_count || 0) === 1 ? "" : "s"}</p>
            </div>
          </div>
          <div data-testid="payments-split-bar" className="mt-4 flex h-2 rounded-full overflow-hidden bg-[#F5EFE6]">
            {splitTotal > 0 ? (
              <>
                <div className="bg-[#C71E2E] transition-all" style={{ width: `${(split.card / splitTotal) * 100}%` }} />
                <div className="bg-[#1A1A1A] transition-all" style={{ width: `${(split.paypal / splitTotal) * 100}%` }} />
                <div className="bg-[#C9A227] transition-all" style={{ width: `${((split.offline || 0) / splitTotal) * 100}%` }} />
              </>
            ) : (
              <div className="w-full bg-[#E5DED5]" />
            )}
          </div>
          <div className="mt-2 flex gap-4 text-[10px] font-ui tracking-[0.12em] uppercase text-[#4A4A4A]">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#C71E2E]" />Card</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#1A1A1A]" />PayPal</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#C9A227]" />Wire / Manual</span>
          </div>
        </div>
      )}

      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table data-testid="payments-table">
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Date</TableHead>
              <TableHead>Booking</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Refund</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 && (
              <TableRow className={ROW}>
                <TableCell colSpan={8} data-testid="payments-empty" className="text-muted-foreground font-ui py-10 text-center">No transactions yet.</TableCell>
              </TableRow>
            )}
            {payments.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.id}`} className={ROW}>
                <TableCell className="font-ui text-sm whitespace-nowrap">
                  {p.created_at ? new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </TableCell>
                <TableCell className="font-ui font-semibold text-[#C71E2E] whitespace-nowrap">{p.booking_ref}</TableCell>
                <TableCell>
                  <p className="font-ui text-sm">{p.customer_name || "—"}</p>
                  <p className="text-xs text-muted-foreground">{p.customer_email}</p>
                </TableCell>
                <TableCell className="font-ui text-sm">{METHOD_LABEL[p.method] || p.method}{p.offline && <span className="ml-1.5 rounded-full border border-[#C9A227]/40 bg-amber-50 px-1.5 py-px text-[9px] font-ui font-semibold uppercase tracking-wide text-amber-700">offline</span>}</TableCell>
                <TableCell className="font-ui text-sm">{KIND_LABEL[p.kind] || KIND_LABEL.booking}</TableCell>
                <TableCell className="font-ui font-semibold whitespace-nowrap">{fmtMoney(p.amount)}</TableCell>
                <TableCell>
                  <span className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 whitespace-nowrap ${
                    p.refunded
                      ? "text-red-700 border-red-200 bg-red-50"
                      : p.payment_status === "paid"
                        ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                        : "text-amber-700 border-amber-200 bg-amber-50"
                  }`}>
                    {p.refunded ? "Refunded" : p.payment_status === "paid" ? "Paid" : "Pending"}
                  </span>
                  {p.refunded && p.refund_note && <p className="mt-1 text-xs text-muted-foreground">{p.refund_note}</p>}
                </TableCell>
                <TableCell className="text-right">
                  {p.payment_status === "paid" && (
                    <Button
                      data-testid={`payment-refund-toggle-${p.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => p.offline ? toggleOfflineRefund(p) : toggleRefund(p)}
                      className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui"
                    >
                      <RotateCcw size={13} className="mr-1.5" />
                      {p.refunded ? "Undo" : "Mark refunded"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
